import type { IOrderItem } from "@/data/model/Order";
import { Format } from "@/data/util/Format";

import { Component } from "@/shared/elements/Component";
import { renderItemIconsHtml } from "@/shared/helpers/AttributeHelper";
import { DiscountUtil } from "@/shared/utils/DiscountUtil";
import { toMoney } from "@/shared/utils/format";

import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";

import { OptionGroup } from "./OptionGroup";

export class OrderItem extends Component {
    public readonly orderItem: IOrderItem;
    public readonly displayPrice: boolean;

    constructor(orderItem: IOrderItem, displayPrice: boolean = false) {
        super();
        this.orderItem = orderItem;
        this.displayPrice = displayPrice;

        this.$element = this.drawElement();
    }

    protected drawElement() {
        const orderItem = this.orderItem;

        // draw order item fields
        const $element = $('<div class="main-item item" />');
        if (orderItem.hasDiscount) {
            $element.addClass("has-discount");
        }
        const $head = $('<div class="head" />').appendTo($element);

        if (orderItem.isCancelled) $element.addClass("cancelled");

        const $quantity = $('<div class="quantity" />').appendTo($head);
        if (orderItem.cancelledQuantity > 0)
            $quantity.append(`<span class="cancelled">${orderItem.originalTotalQuantity}x</span>`);

        if (orderItem.currentTotalQuantity > 0) $quantity.append(`<span>${orderItem.currentTotalQuantity}x</span>`);

        $head.append(
            `<div class="main-name name">${this.getDisplayName()}${renderItemIconsHtml(orderItem.articleId)}</div>`
        );

        if (this.displayPrice) {
            if (this.orderItem.hasDiscount && !this.orderItem.isCancelled && this.orderItem.unitPrice != 0) {
                const icon = DiscountUtil.getDiscountIcon(this.orderItem.lastDiscountType);

                $head.append(`<div class="price has-discount">
                    <div class="price-before-discount">
                        <span class="discount-type">
                            <i class="${icon}"></i>
                        </span>
                        ${this.getDisplayPrice()}
                    </div>
                    <div class="price-after-discount">
                        ${toMoney(this.orderItem.currentTotalAmountAfterDiscount, true)}
                    </div>
                </div>`);
            } else {
                $head.append(`<div class="price">${this.getDisplayPrice()}</div>`);
            }
        }

        // draw option groups
        if (orderItem.options.length) {
            const $subContainer = $('<div class="subs" />').appendTo($element);
            for (const optionGroup of orderItem.options) {
                OptionGroup.render($subContainer, optionGroup, orderItem.isCancelled, this.displayPrice);
            }
        }

        if (orderItem.comment) {
            const $comment = $('<div class="comment" />').appendTo($element);
            $comment.append(`<div>${orderItem.comment.text}</div>`);
        }

        return $element;
    }

    private getDisplayName() {
        return Format.htmlEscape(getNameInLocationLanguage(this.orderItem, true));
    }

    private getDisplayPrice() {
        if (this.orderItem.unitPrice === 0) return "";

        return toMoney(
            this.orderItem.isCancelled ? this.orderItem.originalTotalAmount : this.orderItem.currentTotalAmount,
            true
        );
    }

    public static render($appendToTarget: JQuery, orderItem: IOrderItem, displayPrice: boolean = false): OrderItem {
        const component = new OrderItem(orderItem, displayPrice);
        component.renderTo($appendToTarget);

        return component;
    }
}
